import {
  Badge,
  BadgeIndicator,
  BadgeIndicatorProps,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Divider,
  Flex,
  Kbd,
  Text,
  Tooltip,
} from 'components'
import { generatePath, useNavigate, useParams } from 'react-router'
import { useTheme } from 'utils'
import { RoutePath } from '../utils/routing'
import { SideMenuItem } from '../utils/sideMenuUtils'

interface Props extends SideMenuItem {
  buttonProps?: ButtonProps
  isCollapsed?: boolean
  onNavigate?: () => void
}

export default function SideMenuButton({
  buttonProps,
  comingSoon,
  disabled,
  isCollapsed,
  isCurrent,
  LeftIcon,
  onNavigate,
  page,
  statusDot,
  subItems,
  shortcutKey,
  text,
}: Props) {
  const { theme } = useTheme()
  const { workspaceId } = useParams()
  const navigate = useNavigate()

  const _statusDot =
    statusDot || (isCurrent ? undefined : subItems?.find(item => item.statusDot)?.statusDot)
  const IconContainer =
    _statusDot && isCollapsed
      ? (p: BadgeIndicatorProps) => (
          <BadgeIndicator value badgeProps={{ bg: `${_statusDot}.400` }} {...p} />
        )
      : (p: BoxProps) => <Box lineHeight={1} {...p} />

  return (
    <Flex w="100%" direction="column">
      <Tooltip
        content={
          <Flex direction="column" gap={shortcutKey && comingSoon ? 2 : 0}>
            {shortcutKey && !comingSoon && (
              <Flex align="center" gap={2}>
                <Text>Go to {text}</Text>
                <Text opacity={0.8}>
                  <Kbd>G</Kbd> then <Kbd>{shortcutKey.toUpperCase()}</Kbd>
                </Text>
              </Flex>
            )}
            {comingSoon && <Text>{comingSoon}</Text>}
          </Flex>
        }
        disabled={!comingSoon && !shortcutKey}
        positioning={{ placement: 'right' }}
      >
        <Button
          bg={isCurrent && theme === 'dark' && !subItems ? 'rgba(255, 255, 255, 0.2)' : undefined}
          color={theme === 'light' ? (isCurrent ? 'primary.500' : 'black') : undefined}
          disabled={disabled}
          px={isCollapsed ? 0 : 5}
          mx={isCollapsed ? 2 : 3}
          size={isCollapsed ? 'md' : 'lg'}
          gap={!isCollapsed ? 2 : 0}
          justifyContent={!isCollapsed ? 'start' : 'center'}
          borderRadius="sm"
          variant="ghost"
          leftIcon={
            LeftIcon ? (
              <IconContainer>
                <LeftIcon fontSize="sm" />
              </IconContainer>
            ) : undefined
          }
          {...buttonProps}
          onClick={e => {
            if (!workspaceId || disabled) return
            navigate({ pathname: generatePath(page, { workspaceId, id: null }) })
            buttonProps?.onClick?.(e)
            onNavigate?.()
          }}
        >
          {!isCollapsed && (
            <Flex align="center" gap={2}>
              <Flex align="center" gap={2}>
                <Text lineHeight={1} textStyle="bodySmall">
                  {text}
                </Text>
                {_statusDot && (
                  <Box height="8px" width="8px" borderRadius="full" bg={`${_statusDot}.400`} />
                )}
              </Flex>
              {comingSoon && (
                <Badge colorPalette="primary" size="sm" variant="subtle">
                  Beta
                </Badge>
              )}
            </Flex>
          )}
        </Button>
      </Tooltip>

      {isCurrent && !isCollapsed && (subItems?.length || false) && (
        <Flex pl={10} pr={3} gap={0} flex={1}>
          <Divider
            orientation="vertical"
            borderColor={theme === 'light' ? 'black' : 'white'}
            opacity={theme === 'light' ? 0.1 : 0.2}
          />
          <Flex direction="column" flex={1}>
            {subItems.map(({ isCurrent, disabled, text, page, statusDot }, index) => {
              return (
                <Button
                  key={index}
                  bg={
                    isCurrent
                      ? theme === 'light'
                        ? 'rgba(60, 75, 211, 0.1)'
                        : 'rgba(255, 255, 255, 0.2)'
                      : undefined
                  }
                  variant="ghost"
                  ml={2}
                  size="sm"
                  borderRadius="sm"
                  color={isCurrent ? (theme === 'light' ? 'primary.500' : 'white') : undefined}
                  disabled={disabled}
                  onClick={() => {
                    if (!workspaceId) return
                    const [basePath, hash] = page.split('#')
                    navigate({
                      pathname: generatePath(basePath as RoutePath, { workspaceId, id: null }),
                      hash,
                    })
                    onNavigate?.()
                  }}
                  justifyContent="flex-start"
                >
                  <Text lineHeight={1}>{text}</Text>
                  {statusDot && (
                    <Box
                      ml={2}
                      height="8px"
                      width="8px"
                      borderRadius="full"
                      bg={`${statusDot}.400`}
                    />
                  )}
                </Button>
              )
            })}
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}
